import axios from 'axios';

/**
 * Chapter-step reference data (chapters, application windows, cohort sizes), shared so the
 * Apply container can prefetch it while the applicant is still on the Info step.
 *
 * Without the prefetch, pressing Begin faded Info out and then painted a "Loading chapters..."
 * spinner screen for the length of three sequential requests before the step itself, which
 * read as a second screen flashing past. The step now renders the last result straight away
 * and refreshes it in the background, so a revisit never shows the spinner either.
 */

let cached = null;
let inFlight = null;

// Turns a synchronous throw into a rejection so one bad request cannot sink the others.
const get = (url) => new Promise((resolve) => resolve(axios.get(url)));

const warnAndDefault = (label) => (error) => {
  console.warn(`Failed to fetch ${label}:`, error);
  return [];
};

export function loadChapterStepData() {
  if (inFlight) return inFlight;
  inFlight = Promise.all([
    get('/api/chapters')
      // Unassigned (ID 0) is not a real chapter.
      .then((r) => (r.data || []).filter((chapter) => chapter.id !== 0))
      .catch(warnAndDefault('chapters')),
    get('/api/application_windows/all')
      .then((r) => r.data || [])
      .catch(warnAndDefault('application windows')),
    get('/api/cohort_size')
      .then((r) => r.data || [])
      .catch(warnAndDefault('chapter cohorts')),
  ])
    .then(([chapters, applicationWindows, chapterCohorts]) => {
      cached = { chapters, applicationWindows, chapterCohorts };
      return cached;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** The most recent result, if any, for a spinner-free first render. */
export function peekChapterStepData() {
  return cached;
}

export function prefetchChapterStepData() {
  if (!cached) loadChapterStepData().catch(() => {});
}

export function resetChapterStepDataCache() {
  cached = null;
  inFlight = null;
}
